/* Belvoir PDP benefits: draggable Shopify blocks presented as rows, icons or an accessible carousel. */
(() => {
  const selector = '.product-information';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mobileMedia = window.matchMedia('(max-width: 749px)');
  const labels = () => {
    const lang = (document.documentElement.lang || 'en').toLowerCase();
    if (lang.startsWith('ar')) return { previous: 'السابق', next: 'التالي', slide: 'ميزة رقم' };
    if (lang.startsWith('fr')) return { previous: 'Précédent', next: 'Suivant', slide: 'Avantage' };
    return { previous: 'Previous', next: 'Next', slide: 'Benefit' };
  };

  function mount(info) {
    if (!info || info.dataset.bvBenefitsMounted === 'true') return;
    const config = info.closest('.shopify-section')?.querySelector('[data-bv-benefits-config]');
    if (!config) return;
    const items = [...info.querySelectorAll('.bv-benefit-row[data-bv-benefit-item]')];
    if (!items.length) return;
    const parent = items[0].parentElement;
    if (!parent || items.some(item => item.parentElement !== parent)) return;

    const layout = document.createElement('div');
    layout.className = 'bv-benefits-layout';
    layout.style.setProperty('--bv-benefits-gap', (parseInt(config.dataset.gap, 10) || 12) + 'px');
    layout.style.setProperty('--bv-benefits-radius', (parseInt(config.dataset.radius, 10) || 20) + 'px');
    layout.style.setProperty('--bv-benefits-height', (parseInt(config.dataset.height, 10) || 106) + 'px');
    const track = document.createElement('div');
    track.className = 'bv-benefits-track';
    track.setAttribute('role', 'region');
    track.setAttribute('aria-roledescription', 'carousel');
    track.setAttribute('aria-label', 'Product benefits');
    const controls = document.createElement('div');
    controls.className = 'bv-benefits-controls';
    const prev = document.createElement('button');
    prev.type = 'button';
    prev.className = 'bv-benefits-prev';
    prev.textContent = '‹';
    const dots = document.createElement('div');
    dots.className = 'bv-benefits-dots';
    const next = document.createElement('button');
    next.type = 'button';
    next.className = 'bv-benefits-next';
    next.textContent = '›';
    const l = labels();
    prev.setAttribute('aria-label', l.previous);
    next.setAttribute('aria-label', l.next);
    controls.append(prev, dots, next);
    layout.append(track, controls);
    parent.insertBefore(layout, items[0]);
    items.forEach(item => track.appendChild(item));
    info.dataset.bvBenefitsMounted = 'true';

    let current = 0;
    let timer = null;
    const all = [...track.children].filter(el => el.matches('.bv-benefit-row'));
    all.forEach((el, i) => {
      el.setAttribute('role', 'group');
      el.setAttribute('aria-roledescription', 'slide');
      el.setAttribute('aria-label', (i + 1) + ' / ' + all.length);
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'bv-benefits-dot';
      dot.setAttribute('aria-label', l.slide + ' ' + (i + 1));
      dot.addEventListener('click', () => go(i));
      dots.appendChild(dot);
    });
    const buttons = [...dots.children];

    function paint() {
      buttons.forEach((btn, i) => {
        const active = i === current;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-current', active ? 'true' : 'false');
      });
      prev.disabled = all.length <= 1;
      next.disabled = all.length <= 1;
    }
    function go(i) {
      current = (i + all.length) % all.length;
      const left = all[current].offsetLeft - all[0].offsetLeft;
      track.scrollTo({ left, behavior: reducedMotion.matches ? 'instant' : 'smooth' });
      paint();
    }
    function stopTimer() {
      if (timer) { window.clearInterval(timer); timer = null; }
    }
    function startTimer() {
      stopTimer();
      if (layout.dataset.layout !== 'carousel' || config.dataset.autoplay !== 'true' ||
          reducedMotion.matches || window.Shopify?.designMode || all.length < 2) return;
      const ms = Math.max(3, parseInt(config.dataset.speed, 10) || 5) * 1000;
      timer = window.setInterval(() => go(current + 1), ms);
    }
    function refresh() {
      const mode = mobileMedia.matches ? config.dataset.mobile : config.dataset.desktop;
      const normalized = ['rows', 'icons', 'carousel'].includes(mode) ? mode : 'carousel';
      if (layout.dataset.layout !== normalized) {
        layout.dataset.layout = normalized;
        current = 0;
        track.scrollLeft = 0;
      }
      layout.dataset.dots = config.dataset.dots;
      layout.dataset.arrows = config.dataset.arrows;
      paint();
      startTimer();
    }

    prev.addEventListener('click', () => go(current - 1));
    next.addEventListener('click', () => go(current + 1));
    let scrollFrame;
    track.addEventListener('scroll', () => {
      if (layout.dataset.layout !== 'carousel') return;
      window.cancelAnimationFrame(scrollFrame);
      scrollFrame = window.requestAnimationFrame(() => {
        const left = track.scrollLeft;
        let closest = 0;
        let delta = Infinity;
        all.forEach((el, i) => {
          const distance = Math.abs((el.offsetLeft - all[0].offsetLeft) - left);
          if (distance < delta) { delta = distance; closest = i; }
        });
        current = closest; paint();
      });
    }, { passive: true });
    layout.addEventListener('mouseenter', stopTimer);
    layout.addEventListener('mouseleave', startTimer);
    layout.addEventListener('focusin', stopTimer);
    layout.addEventListener('focusout', () => window.setTimeout(startTimer, 200));
    document.addEventListener('visibilitychange', () => document.hidden ? stopTimer() : startTimer());
    window.addEventListener('resize', refresh, { passive: true });
    document.addEventListener('shopify:block:select', (e) => {
      const row = e.target?.closest?.('.bv-benefit-row');
      if (row && layout.contains(row)) {
        stopTimer();
        const index = all.indexOf(row);
        if (index >= 0) go(index);
      }
    });
    refresh();
  }
  const scan = (root = document) => {
    if (root.matches?.(selector)) mount(root);
    root.querySelectorAll?.(selector).forEach(mount);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan());
  else scan();
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
})();
